<template>
  <div class="d-flex flex-column ga-2 edit-block" :class="{'mb-2': tapStore.view, 'flex-a': (tapStore.view && !editProfile) || tapStore.currentTapLang?.edited}">
    <TapHeader :name="headerName"/>
    <TapTopInfo v-if="!tapStore.currentTapLang?.edited"/>
    <v-expand-transition>
      <div class="publish-progress" v-if="false">
        <div class="publish-progress__text">
          {{ completionText }}
        </div>
        <div class="publish-progress__track">
          <div
              class="publish-progress__bar"
              :style="{ width: `${completion.percent}%`, backgroundColor: completion.color }"
          />
        </div>
      </div>
    </v-expand-transition>
    <TapBlocks/>
    <LangDisabled v-if="tapStore.currentTapLang?.edited"/>

    <div class="pl-6 pr-6 pt-4 pb-4 ga-2 chooser d-flex flex-column" v-if="addBlockInProgress" ref="chooser">
      <div class="d-flex justify-end">
        <v-icon :icon="CloseIcon" class="cursor-pointer" @click="addBlockInProgress=false"/>
      </div>
      <ComponentChooser @chose="addBlockInProgress=false"/>
      <div class="missing-block-btn text-center cursor-pointer" @click="openMissingBlockForm">
        {{ $t('Не нашли нужный блок?') }}
      </div>

    </div>

    <div class="pa-4" v-if="!tapStore.view && !tapStore.currentTapLang?.edited">
      <v-btn variant="flat" :color="tapStore.currentTapLang?.socials?.length ? 'primary' : 'secondary'" rounded="lg"
             class="w-100" @click="addComponent" v-if="!addBlockInProgress">
        <template v-slot:prepend>
          <v-icon :icon="AddIcon" :color="tapStore.currentTapLang?.socials?.length ? 'white' : '#6C757D'" size="24"/>
        </template>
        {{ $t('Добавить блок') }}
      </v-btn>
    </div>
  </div>
  <div v-if="tapStore.view && !editProfile" class="pa-6 powered-by">
    <div v-html="$t('Сделано на {host}', {link: `https://${host}`, host})"></div>
    <div class="report-page mt-3" @click="openReportForm">
      {{ $t('Пожаловаться на страницу') }}
    </div>
  </div>
</template>

<script setup lang="ts">

import {computed, ref} from "vue";
import {useRoute} from "vue-router";
import CloseIcon from "~/assets/icons/close.vue";
import AddIcon from "~/assets/icons/add.vue";

import {useTapStore} from '@/stores/tapStore';
import ComponentChooser from "@/components/ComponentChooser.vue";
import TapHeader from "@/components/tap/TapHeader.vue";
import TapTopInfo from "@/components/tap/TapTopInfo.vue";
import TapBlocks from "@/components/tap/TapBlocks.vue";
import {useI18n} from "vue-i18n";
import LangDisabled from "@/components/tap/LangDisabled.vue";
import {useDialogStore} from "@/stores/dialogStore";
import ReportForm from "@/components/dialogs/ReportForm.vue";
import MissingBlockForm from "@/components/dialogs/MissingBlockForm.vue";

const config = useRuntimeConfig()
const host = config.public.tapLink;
const tapStore = useTapStore();
const dialogStore = useDialogStore();
const route = useRoute()
const chooser = ref(null)
const addBlockInProgress = ref(false);

const editProfile = computed(() => /^\/profile\/[^\/]+\/(edit|view)(\/(ru|kz|en))?\/?$/.test(route.path))

function openReportForm() {
  dialogStore.open(ReportForm)
}

function openMissingBlockForm() {
  dialogStore.open(MissingBlockForm)
}

const addComponent = () => {
  addBlockInProgress.value = true;
  setTimeout(() => {
    chooser.value?.scrollIntoView({
      behavior: 'smooth',
      blockId: 'start',
    })
  }, 11)
}

const { locale, t } = useI18n()
const { params } = useRoute()
const staticAlias = params.alias as string
const staticView = params.view as string
const staticLang = params.lang as string
const staticMode = params.mode === 'view'
const staticEditMode = params.mode === 'edit'
const headerName = computed(() => tapStore.currentTap?.alias || staticAlias || '')

const repeatableCompletionKeys = ['buttons', 'links', 'contacts', 'faq', 'items', 'questions']
const ignoredCompletionKeys = new Set([
  'id',
  'uniqueId',
  'orderIndex',
  'delete',
  'time',
  'type',
  'align',
  'color',
  'radius',
  'social',
  'showSocial',
  'changed',
  'hidden'
])

function hasCompletionValue(value: any): boolean {
  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  if (Array.isArray(value)) {
    return value.some(item => hasCompletionValue(item))
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).some(([key, item]) => {
      return !ignoredCompletionKeys.has(key) && hasCompletionValue(item)
    })
  }

  return false
}

function countRepeatableItems(items: any): number {
  if (!Array.isArray(items)) {
    return 0
  }

  return items.filter(item => !item?.delete && hasCompletionValue(item)).length
}

function countComponentCompletion(component: any): number {
  const data = component?.data

  if (!data) {
    return 0
  }

  const repeatableCount = repeatableCompletionKeys.reduce((count, key) => {
    return count + countRepeatableItems(data[key])
  }, 0)

  if (repeatableCount > 0) {
    return repeatableCount
  }

  return hasCompletionValue(data) ? 1 : 0
}

const completion = computed(() => {
  const lang = tapStore.currentTapLang
  const total = 5
  const socialsCount = countRepeatableItems(lang?.socials)
  const completionCount = lang?.blocks?.reduce((count, block) => {
    const blockCount = block?.blockItems?.reduce((itemsCount, component) => {
      return itemsCount + countComponentCompletion(component)
    }, 0) || 0

    return count + blockCount
  }, 0) || 0
  const filledCount = Math.min(socialsCount + completionCount, total)
  const percent = Math.round((filledCount / total) * 100)

  let color = '#D11635'
  if (percent >= 80) {
    color = '#05A660'
  } else if (percent >= 60) {
    color = '#0D87EF'
  } else if (percent >= 40) {
    color = '#FFCE31'
  }

  return {
    filledCount,
    total,
    percent,
    color
  }
})

const completionText = computed(() => {
  const values = {
    percent: completion.value.percent,
    count: completion.value.filledCount,
    total: completion.value.total
  }

  if (locale.value === 'ru') {
    return `Страница заполнена на ${values.percent}% • ${values.count} из ${values.total} блоков`
  }

  return t('Страница заполнена на {percent}% • {count} из {total} блоков', values)
})

const showCompletionProgress = computed(() => {
  return !tapStore.view &&
      !tapStore.currentTapLang?.edited &&
      completion.value.filledCount > 0 &&
      completion.value.filledCount < completion.value.total
})

const { error } = await useAsyncData(() => `tap-${staticAlias || staticView}-${staticLang}-${staticMode}`,
    async () => {
      tapStore.view = staticMode || !!staticView
      if (staticView) {
        await tapStore.loadPublicFullTap(staticView, staticLang)
      } else if (staticAlias) {
        const hasListedTap = tapStore.tapList.some(tap => tap.alias === staticAlias)
        if (!tapStore.isInitialized || !hasListedTap) {
          await tapStore.getTapList({preserveCurrentTap: true})
        }
        await tapStore.loadFullTap(staticAlias, staticLang)
        if (staticEditMode) {
          const projectId = tapStore.currentTap?.id ?? tapStore.currentTap?.projectId
          if (projectId != null) {
            await tapStore.markProjectOpened(projectId)
          }
        }
      }
      return tapStore.currentTap
    }
)

if (error.value?.statusCode === 404) {
  navigateTo('/404', { redirectCode: 302 })
}
if (error.value?.statusCode === 400) {
  navigateTo(`/${staticView}`, { redirectCode: 302 })
}
if (error.value?.statusCode === 500) {
  navigateTo(`/${staticView}`, { redirectCode: 302 })
}

if(route.params.view) {
  watchEffect(() => {
    const curLang = staticLang?.toUpperCase() || tapStore?.currentTap?.projectVersion?.defaultLang
    if (!curLang) return
    locale.value = curLang === 'KZ' ? 'kk' : curLang.toLowerCase()
  })
}
</script>

<style scoped lang="sass">
.chooser
  background: #FFF
  border-radius: 20px

.edit-block
  &.flex-a
    flex: auto

.publish-progress
  margin: 0 0 8px
  border-radius: 20px
  background: #FFF
  padding: 24px 20px
  &__text
    color: #363636
    font-size: 14px
    font-weight: 400
    line-height: 18px
    margin-bottom: 12px
  &__track
    width: 100%
    height: 8px
    border-radius: 50px
    background: #F1F4F5
    overflow: hidden
  &__bar
    height: 100%
    border-radius: 50px

.powered-by
  border-radius: 20px 20px 0 0
  background: #FFF
  color: #363636
  text-align: center
  font-size: 16px
  font-style: normal
  font-weight: 500
  line-height: normal
  :deep(a)
    color: #363636

.report-page
  color: #B2B7BD
  cursor: pointer
  font-size: 12px

.missing-block-btn
  color: #6C757D
  font-size: 14px
  line-height: normal
  padding: 16px
</style>
